/* eslint-disable @typescript-eslint/no-explicit-any */
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { inject, Inject, Injectable, Optional, PLATFORM_ID, REQUEST, REQUEST_CONTEXT, RESPONSE_INIT, TransferState } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';

import { environment } from '../../environments/environment';
import { SharingShellContext } from '../../sharing-page-shell';
import {
  BootstrapRequestContext, buildBuzzwavePublicUrl, BUZZWAVE_PUBLIC_PATHS, fetchShortLink, fetchWithBootstrapAuth
} from '../shared/helpers/buzzwave-public-api';
import { isNullOrWhiteSpace } from '../shared/helpers/helper-functions';
import { setShortLinkHttpRedirect } from './helpers/short-link-redirect';
import { PageSpinnerService } from '../shared/components/page-spinner/services/page-spinner.service';
import {
  BUZZWAVE_REDIRECT_URL, CAMPAIGNDATA_KEY, CAMPAIGNFOUND_KEY, CAMPAIGNLOOKUPURL_KEY, CAMPAIGNPARTICIPANTID_KEY, ERRORMESSAGE_KEY,
  GUID_REGEX, INACTIVEPAGE_KEY, ISERROR_KEY, ISSHORTLINK_KEY, LOG_KEY, REDIRECTURL_KEY, REQUESTPARSE_KEY, REQUESTURL_KEY,
  SHAREIMAGEURL_KEY
} from './landing-page.state';

@Injectable({
  providedIn: 'root',
})
export class LandingPageResolverService implements Resolve<any> {
  private readonly spinner = inject(PageSpinnerService);
  constructor(
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: Request | null = null,
    @Optional() @Inject(RESPONSE_INIT) private responseInit: ResponseInit | null = null,
  ) {}

  async resolve(route: ActivatedRouteSnapshot): Promise<any> {
    if (isPlatformServer(this.platformId) && this.requestContext?.sharingShell) {
      this.document.getElementById('buzzwave-initial-loader')?.querySelector('img')?.setAttribute(
        'src', `${environment.buzzwaveAdminAssetsS3Url}/buzzwave-logo-middle.png`,
      );
      this.document.documentElement.setAttribute('data-buzzwave-loading-shell', '');
      return null;
    }
    this.spinner.showLoader();
    try {
      return await this.resolveCampaign(route);
    } finally {
      this.spinner.hideLoader();
    }
  }

  private readonly requestContext = inject(REQUEST_CONTEXT, { optional: true }) as SharingShellContext | null;
  private readonly document = inject(DOCUMENT);

  private async resolveCampaign(route: ActivatedRouteSnapshot): Promise<any> {
    const requestedContext = this.parseCampaignContext(route);
    const cached = this.transferState.get(CAMPAIGNDATA_KEY, null);
    const cachedParticipant = this.transferState.get(CAMPAIGNPARTICIPANTID_KEY, '');
    if (cached && cached.campaignId === requestedContext.campaignId && cachedParticipant === requestedContext.campaignParticipantId) {
      return cached;
    }
    // A new campaign or participant must not inherit an earlier request's payload or error/redirect state.
    for (const key of [CAMPAIGNDATA_KEY, INACTIVEPAGE_KEY, ISERROR_KEY, ISSHORTLINK_KEY, REDIRECTURL_KEY, SHAREIMAGEURL_KEY]) this.transferState.remove(key);

    const requestPath = this.getRequestPath(route);
    const requestUrl = `${environment.buzzwaveGoUrl}${requestPath}`;
    const previewToken = route.queryParamMap.get('previewToken')?.trim() ?? '';
    const safeQueryParams = new URLSearchParams(
      route.queryParams as Record<string, string>,
    );
    safeQueryParams.delete('previewToken');
    const rawQueryString = safeQueryParams.toString();
    const parsedCampaignContext = this.parseCampaignContext(route);
    const requestParse = {
      rawPath: requestPath,
      rawQueryString,
      campaignId: parsedCampaignContext.campaignId,
      campaignParticipantId: parsedCampaignContext.campaignParticipantId,
    };

    this.transferState.set(REQUESTURL_KEY, requestUrl);
    this.transferState.set(REQUESTPARSE_KEY, requestParse);
    if (this.isStaticAssetRequest(requestPath)) {
      return null;
    }
    if (requestPath.startsWith('/.well-known/')) {
      return null;
    }

    const bootstrapContext: BootstrapRequestContext | undefined = isPlatformServer(this.platformId)
      ? {
          origin: environment.buzzwaveGoUrl,
          referer: requestUrl,
          ...(previewToken ? { previewToken } : {}),
        }
      : undefined;

    const { campaignId, campaignParticipantId } = parsedCampaignContext;
    this.transferState.set(CAMPAIGNPARTICIPANTID_KEY, campaignParticipantId);

    if (
      requestPath === '/' &&
      isNullOrWhiteSpace(campaignId) &&
      isNullOrWhiteSpace(campaignParticipantId)
    ) {
      this.transferState.set(REDIRECTURL_KEY, BUZZWAVE_REDIRECT_URL);
      this.transferState.set(LOG_KEY, `No campaign params provided, redirecting to: ${BUZZWAVE_REDIRECT_URL}`);
      return null;
    }

    if (campaignId.length < 5 || (campaignId.length > 7 && !GUID_REGEX.test(campaignId))) {
      console.warn('[LandingPage][Resolver] Invalid campaign id', {
        campaignId,
      });
      this.transferState.set(LOG_KEY, `Invalid campaignID ${campaignId}, redirecting to: ${BUZZWAVE_REDIRECT_URL}`);
      this.transferState.set(CAMPAIGNFOUND_KEY, false);
      this.transferState.set(ISERROR_KEY, true);
      this.transferState.set(ERRORMESSAGE_KEY, 'Invalid campaign ID.');
      return null;
    }

    // Campaign/participant routes identify campaigns directly; only standalone IDs can be short links.
    if (campaignId.length >= 7 && isNullOrWhiteSpace(campaignParticipantId)) {
      this.transferState.set(ISSHORTLINK_KEY, true);
      try {
        const shortLink = await fetchShortLink(campaignId);
        const resolvedCampaignId = shortLink.campaignId ?? campaignId;
        const resolvedCampaignParticipantId = isNullOrWhiteSpace(campaignParticipantId) ? shortLink.campaignParticipantId || '' : campaignParticipantId;

        const redirectUrl = shortLink.redirectUrl ?? shortLink.forwardUrl ?? '';
        if (!isNullOrWhiteSpace(redirectUrl)) {
          this.transferState.set(REDIRECTURL_KEY, redirectUrl);
        }

        const getCampaignUrl = this.buildCampaignUrl(resolvedCampaignId, resolvedCampaignParticipantId);
        this.transferState.set(CAMPAIGNLOOKUPURL_KEY, getCampaignUrl);
        const response = await fetchWithBootstrapAuth(
          getCampaignUrl,
          { method: 'GET' },
          resolvedCampaignId,
          BUZZWAVE_PUBLIC_PATHS.campaign,
          bootstrapContext,
        );

        if (response.status !== 200) {
          const body = await response.text().catch(() => '');
          const parsedBody = this.tryParseJson(body);
          const inactivePageState = this.buildInactivePageState(parsedBody);
          if (inactivePageState) {
            this.transferState.set(INACTIVEPAGE_KEY, inactivePageState);
            this.transferState.set(CAMPAIGNFOUND_KEY, false);
            this.transferState.set(LOG_KEY, `Inactive campaign resolved: ${inactivePageState.campaignStatus}`);
            return null;
          }
          throw new Error(
            `Short link campaign lookup failed with status ${response.status}: ${body || 'no response body'}`,
          );
        }

        const data = (await response.json()) as any;
        const inactivePageState = this.buildInactivePageState(data);
        if (inactivePageState) {
          this.setInactivePageTransferState(inactivePageState);
          return null;
        }
        const shareImageUrl = this.buildShareImageUrl(data);

        if (data.redirect === true) {
          this.transferState.set(LOG_KEY, `Redirect true, redirecting to: ${data.postForwardUrl}`);
          if (isNullOrWhiteSpace(data.postForwardUrl)) {
            this.transferState.set(CAMPAIGNFOUND_KEY, false);
            this.transferState.set(ISERROR_KEY, true);
            this.transferState.set(ERRORMESSAGE_KEY, 'Campaign redirect URL was not provided.');
            return null;
          }
          this.transferState.set(REDIRECTURL_KEY, data.postForwardUrl);
        }

        if (!isNullOrWhiteSpace(data.redirectUrl)) {
          this.transferState.set(REDIRECTURL_KEY, data.redirectUrl);
        }

        this.transferState.set(CAMPAIGNFOUND_KEY, true);
        this.transferState.set(CAMPAIGNDATA_KEY, data);
        this.transferState.set(CAMPAIGNPARTICIPANTID_KEY, resolvedCampaignParticipantId);
        this.transferState.set(SHAREIMAGEURL_KEY, shareImageUrl);

        if (isPlatformServer(this.platformId)) {
          setShortLinkHttpRedirect(this.request, this.responseInit,
            this.transferState.get(REDIRECTURL_KEY, ''), environment.buzzwaveGoUrl, Boolean(previewToken));
        }

        return data;
      } catch (error) {
        this.transferState.set(ISERROR_KEY, true);
        this.transferState.set(ERRORMESSAGE_KEY, 'This sharing link is unavailable. Please check the link or contact the event organiser.');
        if (previewToken) {
          this.setPreviewSessionError();
        }
        console.warn('[LandingPage][Resolver] Short link lookup failed', {
          campaignId,
          errorType: error instanceof Error ? error.name : 'UnknownError',
        });
        this.transferState.set(CAMPAIGNFOUND_KEY, false);
        if (isPlatformServer(this.platformId)) {
          setShortLinkHttpRedirect(this.request, this.responseInit,
            this.transferState.get(REDIRECTURL_KEY, ''), environment.buzzwaveGoUrl, Boolean(previewToken));
        }
        return null;
      }
    } else {

      const getCampaignUrl = this.buildCampaignUrl(campaignId, campaignParticipantId);
      this.transferState.set(CAMPAIGNLOOKUPURL_KEY, getCampaignUrl);

      let response: Response;
      try {
        response = await fetchWithBootstrapAuth(
          getCampaignUrl,
          { method: 'GET' },
          campaignId,
          BUZZWAVE_PUBLIC_PATHS.campaign,
          bootstrapContext,
        );
      } catch (error) {
        if (previewToken) {
          this.setPreviewSessionError();
        }
        const redirectUrl = this.extractRedirectUrlFromError(error);
        if (!isNullOrWhiteSpace(redirectUrl)) {
          this.transferState.set(REDIRECTURL_KEY, redirectUrl);
          this.transferState.set(LOG_KEY, `Campaign lookup returned redirect URL: ${redirectUrl}`);
        }
        console.warn('[LandingPage][Resolver] Campaign lookup failed', {
          campaignId,
          errorType: error instanceof Error ? error.name : 'UnknownError',
        });
        this.transferState.set(CAMPAIGNFOUND_KEY, false);
        return null;
      }

      if (response.status !== 200) {
        const body = await response.text().catch(() => '');
        const parsedBody = this.tryParseJson(body);
        const inactivePageState = this.buildInactivePageState(parsedBody);
        if (inactivePageState) {
          this.setInactivePageTransferState(inactivePageState);
          return null;
        }
        console.warn('[LandingPage][Resolver] Campaign API returned non-200', {
          campaignId,
          status: response.status,
          requestId: response.headers.get('x-request-id') ?? response.headers.get('x-amzn-requestid'),
        });
        this.transferState.set(CAMPAIGNFOUND_KEY, false);
        return null;
      }

      const data = (await response.json()) as any;
      const inactivePageState = this.buildInactivePageState(data);
      if (inactivePageState) {
        this.setInactivePageTransferState(inactivePageState);
        return null;
      }
      const shareImageUrl = this.buildShareImageUrl(data);

      if (data.redirect === true) {
        this.transferState.set(LOG_KEY, `Redirect true, redirecting to: ${data.postForwardUrl}`);
        if (isNullOrWhiteSpace(data.postForwardUrl)) {
          this.transferState.set(CAMPAIGNFOUND_KEY, false);
          this.transferState.set(ISERROR_KEY, true);
          this.transferState.set(ERRORMESSAGE_KEY, 'Campaign redirect URL was not provided.');
          return null;
        }
        this.transferState.set(REDIRECTURL_KEY, data.postForwardUrl);
      }

      if (!isNullOrWhiteSpace(data.redirectUrl)) {
        this.transferState.set(REDIRECTURL_KEY, data.redirectUrl);
      }

      this.transferState.set(CAMPAIGNFOUND_KEY, true);
      this.transferState.set(CAMPAIGNDATA_KEY, data);
      this.transferState.set(CAMPAIGNPARTICIPANTID_KEY, campaignParticipantId);
      this.transferState.set(SHAREIMAGEURL_KEY, shareImageUrl);

      return data;
    }
  }

  private extractRedirectUrlFromError(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return '';
    }

    const directRedirectUrl = (error as { redirectUrl?: unknown }).redirectUrl;
    if (typeof directRedirectUrl === 'string' && !isNullOrWhiteSpace(directRedirectUrl)) {
      return directRedirectUrl;
    }

    const message = (error as { message?: unknown }).message;
    if (typeof message !== 'string' || isNullOrWhiteSpace(message)) {
      return '';
    }

    const jsonStart = message.indexOf('{');
    const jsonEnd = message.lastIndexOf('}');
    if (jsonStart < 0 || jsonEnd <= jsonStart) {
      return '';
    }

    try {
      const parsed = JSON.parse(message.slice(jsonStart, jsonEnd + 1)) as { redirectUrl?: unknown };
      return typeof parsed.redirectUrl === 'string' && !isNullOrWhiteSpace(parsed.redirectUrl)
        ? parsed.redirectUrl
        : '';
    } catch {
      return '';
    }
  }

  private setPreviewSessionError(): void {
    this.transferState.set(CAMPAIGNFOUND_KEY, false);
    this.transferState.set(ISERROR_KEY, true);
    this.transferState.set(
      ERRORMESSAGE_KEY,
      'This preview session has expired or is no longer valid. Close the preview and open it again.',
    );
  }

  private tryParseJson(body: string): any | null {
    if (isNullOrWhiteSpace(body)) {
      return null;
    }

    try {
      return JSON.parse(body);
    } catch {
      return null;
    }
  }

  private buildInactivePageState(data: any): {
    campaignStatus: string;
    eventName: string;
    logoUrl: string;
    redirectUrl: string;
    message: string;
  } | null {
    if (!data || typeof data !== 'object') {
      return null;
    }

    const campaignStatus = typeof data.campaignStatus === 'string' ? data.campaignStatus.trim() : '';
    const eventName =
      typeof data.eventName === 'string'
        ? data.eventName.trim()
        : typeof data.campaignName === 'string'
          ? data.campaignName.trim()
          : '';
    const logoUrl = typeof data.logoUrl === 'string' ? data.logoUrl.trim() : '';
    const redirectUrl = typeof data.redirectUrl === 'string' ? data.redirectUrl.trim() : '';
    const responseMessage = typeof data.message === 'string' ? data.message.trim() : '';

    const normalizedStatus = campaignStatus.toLowerCase();
    const normalizedMessage = responseMessage.toLowerCase();
    const looksInactive =
      Boolean(data.error) ||
      normalizedStatus === 'paused' ||
      normalizedStatus === 'completed' ||
      normalizedStatus === 'inactive' ||
      normalizedMessage.includes('campaign is paused') ||
      normalizedMessage.includes('campaign is completed') ||
      normalizedMessage.includes('not currently active') ||
      normalizedMessage.includes('no longer active');

    if (!looksInactive) {
      return null;
    }

    const inactiveMessage = this.buildInactivePageMessage(campaignStatus);

    return {
      campaignStatus,
      eventName,
      logoUrl,
      redirectUrl,
      message: inactiveMessage,
    };
  }

  private setInactivePageTransferState(inactivePageState: {
    campaignStatus: string;
    eventName: string;
    logoUrl: string;
    redirectUrl: string;
    message: string;
  }): void {
    this.transferState.set(INACTIVEPAGE_KEY, inactivePageState);
    this.transferState.set(REDIRECTURL_KEY, '/inactive');
    this.transferState.set(CAMPAIGNFOUND_KEY, false);
    this.transferState.set(LOG_KEY, `Inactive campaign resolved: ${inactivePageState.campaignStatus}`);
  }

  private buildInactivePageMessage(campaignStatus: string): string {

    const normalizedStatus = campaignStatus.trim().toLowerCase();

    if (normalizedStatus === 'completed') {
      return 'Event promotion has ended. Thank you for your support.';
    } else if (normalizedStatus === 'paused') {
      return 'Event promotion is currently paused. Please check back later.';
    }

    return 'Event promotion is not currently active. Please check again later.';
  }

  private parseCampaignContext(route: ActivatedRouteSnapshot): { campaignId: string; campaignParticipantId: string } {
    const params = this.collectRouteParams(route);
    return {
      campaignId: params['campaignId'] ?? '',
      campaignParticipantId: params['campaignParticipantId'] ?? '',
    };
  }

  private collectRouteParams(route: ActivatedRouteSnapshot): Record<string, string> {
    const params: Record<string, string> = {};
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      Object.assign(params, current.params);
      current = current.firstChild ?? null;
    }

    return params;
  }

  private isStaticAssetRequest(requestPath: string): boolean {
    return requestPath.startsWith('/assets/') || /\.[a-zA-Z0-9]{1,8}$/.test(requestPath);
  }

  private getRequestPath(route: ActivatedRouteSnapshot): string {
    const segments = route.pathFromRoot
      .flatMap((snapshot) => snapshot.url.map((segment) => segment.path))
      .filter(Boolean);

    return `/${segments.join('/')}`;
  }

  private buildCampaignUrl(campaignId: string, campaignParticipantId: string): string {
    let getCampaignUrl = `${buildBuzzwavePublicUrl(BUZZWAVE_PUBLIC_PATHS.campaign)}?campaignId=${encodeURIComponent(campaignId)}&isTest=false`;
    if (!isNullOrWhiteSpace(campaignParticipantId)) {
      getCampaignUrl += `&campaignParticipantId=${campaignParticipantId}`;
    }
    return getCampaignUrl;
  }

  private buildShareImageUrl(data: any): string {
    const base = environment.buzzwaveClientAssetsS3Url;
    let shareImageUrl = '';
    if (data.showCustomImage ?? false) {
      shareImageUrl = isNullOrWhiteSpace(data.participantPostImageUrl)
        ? `${base}/${data.postImageUrl}`
        : `${base}/${data.participantPostImageUrl}`;
    } else if (data.usePersonalisation) {
      if (!isNullOrWhiteSpace(data.postPersonalisationImagePreviewUrl)) {
        shareImageUrl = `${base}/${data.postPersonalisationImagePreviewUrl}`;
      } else if (!isNullOrWhiteSpace(data.postImageUrl)) {
        shareImageUrl = `${base}/${data.postImageUrl}`;
      } else {
        shareImageUrl = '/assets/defaultImage11.png';
      }
    } else {
      shareImageUrl = isNullOrWhiteSpace(data.postImageUrl)
        ? '/assets/defaultImage12.png'
        : `${base}/${data.postImageUrl}`;
    }
    return shareImageUrl.replace(/ /g, '%20');
  }
}
